import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, CheckCheck, ChevronLeft, ChevronRight, Loader2, MessageCircle, Pencil, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ernaehrungAPI } from '@/lib/api';

/* Tagebuch mit Coach-Kommentaren (11.10.2026, Auftrag „Genuss-Planer“ Stufe 2): Der Coach kommentiert einzelne
   Tagebuch-Einträge, das Mitglied antwortet in der App. Dieselbe Karte im CRM am Mitglied und auf der
   Trainer-Webseite — `quelle` kapselt die Aufrufe (wie bei der GenussKarte). Der Server prüft Schalter und Zugriff
   bei jeder Anfrage.
   „Gelesen“ gilt für das ganze Team: Liest einer die Antwort, ist sie für alle gelesen. Wer antwortet, hat gelesen.

   Essensfoto (Stufe 3): Foto-Einträge zeigen das Foto (nur mit Anmeldung geladen, als Blob im Speicher der Seite —
   nie über eine öffentliche Adresse) und lassen sich berichtigen (kcal und Nährwerte; der Verlauf nennt wer und
   wann). Die Karte erscheint mit dem Schalter „Kommentare“ ODER „Essensfoto“; kommentiert wird nur, wenn die
   Einträge `kommentare` tragen (Schalter „Kommentare“). */

const MAHLZEIT = { fruehstueck: 'Frühstück', mittag: 'Mittagessen', abend: 'Abendessen', snack: 'Snack' };
const SICHERHEIT = { hoch: 'hoch', mittel: 'mittel', niedrig: 'niedrig' };
const TEXT_MAX = 500;
const fehlertext = (e, rest = 'Das hat nicht geklappt') => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : rest; };
const tagText = (iso, opt = { weekday: 'short', day: '2-digit', month: '2-digit' }) => new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE', opt);
const plusTage = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
// `am` kommt in UTC — angezeigt wird Ortszeit
const zeitText = (utc) => new Date(utc).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const zahl = (n) => Number(n || 0).toLocaleString('de-DE', { maximumFractionDigits: 1 });

/* Aufrufe für das CRM (Recht ernaehrung.view, wie die Tagebuch-Ansicht; berichtigen und Widerruf: ernaehrung.edit). */
export const tagebuchQuelleCrm = (leadId) => ({
  tag: (datum) => ernaehrungAPI.tagebuch(leadId, datum).then((r) => r.data),
  kommentieren: (eintragId, datum, text) => ernaehrungAPI.kommentar(leadId, eintragId, { datum, text }).then((r) => r.data),
  gelesen: (eintragId, datum) => ernaehrungAPI.kommentareGelesen(leadId, eintragId, { datum }).then((r) => r.data),
  foto: (fotoId, klein) => ernaehrungAPI.essensfoto(leadId, fotoId, klein).then((r) => r.data),
  berichtigen: (eintragId, datum, werte) => ernaehrungAPI.fotoNaehrwerte(leadId, eintragId, { datum, ...werte }).then((r) => r.data),
  widerruf: () => ernaehrungAPI.fotoWiderruf(leadId).then((r) => r.data),
  // E2 (15.10.2026): Kommentar des Coachs zum ganzen Tag — erscheint in der App unter dem Tag
  tageskommentar: (datum, text) => ernaehrungAPI.tageskommentar(leadId, datum, text).then((r) => r.data),
});

function Tageskommentar({ d, datum, quelle, onGeaendert }) {
  const [text, setText] = useState(d.tageskommentar?.text || '');
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setText(d.tageskommentar?.text || ''); }, [d.tageskommentar?.text, datum]);
  const speichern = async () => {
    setLaeuft(true);
    try { await quelle.tageskommentar(datum, text.trim()); toast.success(text.trim() ? 'Kommentar zum Tag gespeichert — das Mitglied bekommt einen Push' : 'Kommentar entfernt'); await onGeaendert(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); } finally { setLaeuft(false); }
  };
  return (
    <div className="rounded-md border border-border/60 p-2 space-y-1.5" data-testid="tageskommentar">
      <p className="text-xs font-medium">Kommentar zum Tag <span className="text-muted-foreground font-normal">(das Mitglied sieht ihn in der App unter diesem Tag)</span></p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={2} placeholder="z. B. Gute Wahl in der Kantine — Soße nächstes Mal extra."
        className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm" aria-label="Kommentar zum Tag" />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={laeuft || text.trim() === (d.tageskommentar?.text || '')} onClick={speichern}>{text.trim() ? 'Speichern' : 'Entfernen'}</Button>
        {d.tageskommentar?.am && <span className="text-xs text-muted-foreground">zuletzt {zeitText(d.tageskommentar.am)}{d.tageskommentar.coach_name ? ` von ${d.tageskommentar.coach_name}` : ''}</span>}
      </div>
    </div>
  );
}

/* Aufrufe für die Trainer-Webseite (`coach` = axios-Client mit dem Trainer-Token). Den Widerruf trägt nur das CRM ein. */
export const tagebuchQuelleCoach = (coach, mid) => ({
  tag: (datum) => coach.get(`/ernaehrung/mitglied/${mid}/tagebuch`, { params: { datum } }).then((r) => r.data),
  kommentieren: (eintragId, datum, text) => coach.post(`/ernaehrung/mitglied/${mid}/tagebuch/${eintragId}/kommentar`, { datum, text }).then((r) => r.data),
  gelesen: (eintragId, datum) => coach.put(`/ernaehrung/mitglied/${mid}/tagebuch/${eintragId}/kommentare/gelesen`, { datum }).then((r) => r.data),
  foto: (fotoId, klein) => coach.get(`/ernaehrung/mitglied/${mid}/foto/${fotoId}`, { params: klein ? { klein: 1 } : {}, responseType: 'blob' }).then((r) => r.data),
  berichtigen: (eintragId, datum, werte) => coach.put(`/ernaehrung/mitglied/${mid}/tagebuch/${eintragId}/naehrwerte`, { datum, ...werte }).then((r) => r.data),
});

function Verlauf({ kommentare }) {
  if (!kommentare.length) return null;
  return (
    <ul className="space-y-1.5" aria-label="Kommentar-Verlauf">
      {kommentare.map((k) => {
        const coach = k.von === 'coach';
        return (
          <li key={k.id} className={`flex ${coach ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm ${coach ? 'bg-primary/10' : 'bg-muted'} ${!k.gelesen ? 'ring-1 ring-primary' : ''}`}>
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                <span>{k.name || (coach ? 'Coach' : 'Mitglied')}{coach ? '' : ' (Mitglied)'} · {zeitText(k.am)}</span>
                {!k.gelesen && <Badge variant="outline" className="border-primary/40 text-primary">neu</Badge>}
              </p>
              <p className="whitespace-pre-line break-words">{k.text}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* Das Foto eines Eintrags: Vorschaubild, auf Klick das große Bild. Geladen wird mit Anmeldung als Blob; die
   Blob-Adresse lebt nur, solange die Karte offen ist. */
function EssensFoto({ fotoId, name, quelle }) {
  const [klein, setKlein] = useState(null);
  const [gross, setGross] = useState(null);
  const [offen, setOffen] = useState(false);
  const [fehlt, setFehlt] = useState(false);
  const adressen = useRef([]);
  const laden = useCallback(async (nurKlein) => {
    const url = URL.createObjectURL(await quelle.foto(fotoId, nurKlein));
    adressen.current.push(url);
    return url;
  }, [quelle, fotoId]);
  useEffect(() => {
    let lebt = true;
    laden(true).then((url) => { if (lebt) setKlein(url); }).catch(() => { if (lebt) setFehlt(true); });
    const liste = adressen.current;
    return () => { lebt = false; liste.forEach((u) => URL.revokeObjectURL(u)); liste.length = 0; };
  }, [laden]);
  const umschalten = async () => {
    if (!offen && !gross) { try { setGross(await laden(false)); } catch { toast.error('Das Foto konnte nicht geladen werden'); return; } }
    setOffen(!offen);
  };
  if (fehlt) return <p className="text-xs text-muted-foreground">Das Foto konnte nicht geladen werden.</p>;
  if (!klein) return <Skeleton className="h-24 w-24 rounded-md" />;
  return (
    <button type="button" onClick={umschalten} aria-expanded={offen} aria-label={offen ? 'Foto verkleinern' : 'Foto vergrößern'}
      className="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <img src={offen && gross ? gross : klein} alt={`Foto des Mitglieds: ${name}`} className={`rounded-md object-cover ${offen ? 'max-h-[28rem] w-auto max-w-full' : 'h-24 w-24'}`} />
    </button>
  );
}

/* kcal und Nährwerte eines Foto-Eintrags berichtigen — der Server hält fest, wer es wann war und was vorher galt. */
function Berichtigen({ e, datum, quelle, onGeaendert }) {
  const start = () => ({ kcal: e.kcal_offen ? '' : String(e.naehrwerte.kcal), eiweiss_g: e.kcal_offen ? '' : String(e.naehrwerte.eiweiss),
    kohlenhydrate_g: e.kcal_offen ? '' : String(e.naehrwerte.kh), fett_g: e.kcal_offen ? '' : String(e.naehrwerte.fett) });
  const [offen, setOffen] = useState(false);
  const [w, setW] = useState(start);
  const [busy, setBusy] = useState(false);
  const num = (t) => (t === '' ? null : Number(String(t).replace(',', '.')));
  const kcal = num(w.kcal);
  const gueltig = kcal !== null && Number.isFinite(kcal) && kcal >= 0 && kcal <= 5000
    && ['eiweiss_g', 'kohlenhydrate_g', 'fett_g'].every((f) => w[f] === '' || (Number.isFinite(num(w[f])) && num(w[f]) >= 0));
  const speichern = async () => {
    setBusy(true);
    try {
      await quelle.berichtigen(e.id, datum, { kcal, eiweiss_g: num(w.eiweiss_g), kohlenhydrate_g: num(w.kohlenhydrate_g), fett_g: num(w.fett_g) });
      toast.success('Berichtigt — steht im Verlauf des Eintrags'); setOffen(false); await onGeaendert();
    } catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };
  const feld = (name, label) => (
    <label className="text-xs text-muted-foreground">{label}
      <Input className="mt-0.5 w-24" inputMode="decimal" value={w[name]} onChange={(ev) => setW({ ...w, [name]: ev.target.value })} />
    </label>
  );
  if (!offen) return <Button size="sm" variant={e.kcal_offen ? 'default' : 'ghost'} className={e.kcal_offen ? '' : '-ml-2'} onClick={() => { setW(start()); setOffen(true); }}><Pencil className="w-4 h-4 mr-1" />{e.kcal_offen ? 'kcal ergänzen' : 'Werte berichtigen'}</Button>;
  return (
    <div className="space-y-2 rounded-md bg-muted/50 p-2">
      <div className="flex flex-wrap items-end gap-2">
        {feld('kcal', 'kcal')}{feld('eiweiss_g', 'Eiweiß (g)')}{feld('kohlenhydrate_g', 'Kohlenhydrate (g)')}{feld('fett_g', 'Fett (g)')}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy || !gueltig} onClick={speichern}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOffen(false)}>Abbrechen</Button>
        <span className="text-xs text-muted-foreground">Leere Nährwerte zählen als 0. Hängt der Eintrag an einer Genussmahlzeit, rechnet der Wochenausgleich neu.</span>
      </div>
    </div>
  );
}

function Eintrag({ e, datum, quelle, onGeaendert }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const [offen, setOffen] = useState(false);
  const mitKommentaren = e.kommentare !== undefined;       // ohne Schalter „Kommentare“ fehlt das Feld
  const kommentare = e.kommentare || [];
  const neu = kommentare.filter((k) => !k.gelesen).length;
  const zeigen = offen || kommentare.length > 0;
  const istFoto = e.art === 'foto';
  const senden = async () => {
    setBusy('senden');
    try { await quelle.kommentieren(e.id, datum, text.trim()); setText(''); toast.success('Kommentar gesendet — das Mitglied bekommt einen Push'); await onGeaendert(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setBusy(''); }
  };
  const gelesen = async () => {
    setBusy('gelesen');
    try { await quelle.gelesen(e.id, datum); await onGeaendert(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setBusy(''); }
  };
  return (
    <li className={`rounded-md border p-3 space-y-2 ${neu ? 'border-primary/50' : ''}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
        <span className="min-w-0 break-words flex flex-wrap items-center gap-x-2">
          <span><span className="text-muted-foreground">{e.mahlzeit ? `${MAHLZEIT[e.mahlzeit]}: ` : ''}</span><span className="font-medium">{e.name}</span>{e.gramm ? ` · ${e.gramm} g` : ''}</span>
          {istFoto && <Badge variant="outline"><Camera className="w-3 h-3 mr-1" aria-hidden="true" />Foto</Badge>}
          {e.genuss_id && <Badge variant="outline">Genussmahlzeit</Badge>}
        </span>
        {e.kcal_offen
          ? <Badge variant="outline" className="border-amber-300 bg-amber-500/10 text-amber-800 shrink-0">kcal offen</Badge>
          : <span className="tabular-nums text-muted-foreground shrink-0">{e.naehrwerte.kcal} kcal</span>}
      </div>
      {istFoto && (
        <div className="flex flex-wrap items-start gap-3">
          {e.foto_id && quelle.foto ? <EssensFoto fotoId={e.foto_id} name={e.name} quelle={quelle} />
            : <p className="text-xs text-muted-foreground">{e.foto_entfernt ? 'Das Foto ist gelöscht (Widerruf oder Ende der Aufbewahrung).' : 'Ohne Foto.'}</p>}
          <div className="min-w-0 flex-1 space-y-1 text-xs text-muted-foreground">
            {!e.kcal_offen && <p className="tabular-nums">Eiweiß {zahl(e.naehrwerte.eiweiss)} g · Kohlenhydrate {zahl(e.naehrwerte.kh)} g · Fett {zahl(e.naehrwerte.fett)} g</p>}
            {e.schaetzung && <p className="tabular-nums">Schätzung aus dem Foto: {e.schaetzung.kcal} kcal (Spanne {e.schaetzung.kcal_von}–{e.schaetzung.kcal_bis}), Sicherheit {SICHERHEIT[e.schaetzung.sicherheit] || e.schaetzung.sicherheit}</p>}
            {e.kcal_offen && <p>Das Mitglied hat keine kcal angegeben — bitte schätzen und ergänzen.</p>}
            {(e.korrekturen || []).length > 0 && (
              <ul aria-label="Verlauf der Berichtigungen">
                {e.korrekturen.map((k) => (
                  <li key={k.am + (k.von_id || '')} className="tabular-nums">Berichtigt von {k.von || 'unbekannt'} am {zeitText(k.am)} — vorher {k.vorher?.kcal_offen ? 'kcal offen' : `${k.vorher?.kcal} kcal`}</li>
                ))}
              </ul>
            )}
            {quelle.berichtigen && <Berichtigen e={e} datum={datum} quelle={quelle} onGeaendert={onGeaendert} />}
          </div>
        </div>
      )}
      {mitKommentaren && <Verlauf kommentare={kommentare} />}
      {mitKommentaren && (!zeigen ? (
        <Button size="sm" variant="ghost" className="-ml-2" onClick={() => setOffen(true)}><MessageCircle className="w-4 h-4 mr-1" />Kommentieren</Button>
      ) : (
        <div className="space-y-1.5">
          <Textarea aria-label={`Kommentar zu ${e.name}`} rows={2} maxLength={TEXT_MAX} value={text} onChange={(ev) => setText(ev.target.value)} placeholder="Kurzer Kommentar — kommt per Push in die App" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={!!busy || !text.trim()} onClick={senden}>{busy === 'senden' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Senden</Button>
            {neu > 0 && <Button size="sm" variant="outline" disabled={!!busy} onClick={gelesen}>{busy === 'gelesen' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1" />}Als gelesen markieren</Button>}
            <span className="text-xs text-muted-foreground tabular-nums ml-auto">{text.length}/{TEXT_MAX}</span>
          </div>
        </div>
      ))}
    </li>
  );
}

/* Stand des Essensfotos am Mitglied: Einwilligung, Anzahl der Fotos — und im CRM der Widerruf, den das Team einträgt
   (z. B. per E-Mail widerrufen). Zwei Schritte statt einer Rückfrage des Browsers. */
function EssensfotoStand({ stand, quelle, onGeaendert }) {
  const [sicher, setSicher] = useState(false);
  const [busy, setBusy] = useState(false);
  const ew = stand.einwilligung;
  const widerrufen = async () => {
    setBusy(true);
    try { const r = await quelle.widerruf(); toast.success(`Widerruf eingetragen — ${r.geloeschte_fotos} Fotos gelöscht`); setSicher(false); await onGeaendert(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" role="status">
      <span className="text-muted-foreground"><Camera className="inline w-4 h-4 mr-1" aria-hidden="true" />Essensfoto:{' '}
        {ew.erteilt ? `Einwilligung erteilt am ${new Date(ew.erteilt_am).toLocaleDateString('de-DE')}`
          : ew.widerrufen_am ? `Einwilligung widerrufen am ${new Date(ew.widerrufen_am).toLocaleDateString('de-DE')}`
            : ew.widerrufbar ? 'Einwilligung zu einer früheren Fassung — das Mitglied muss neu zustimmen' : 'keine Einwilligung'}
        {' · '}<span className="tabular-nums">{stand.fotos}</span> {stand.fotos === 1 ? 'Foto' : 'Fotos'} gespeichert</span>
      {quelle.widerruf && ew.widerrufbar && (!sicher
        ? <Button size="sm" variant="outline" onClick={() => setSicher(true)}>Widerruf eintragen</Button>
        : <>
          <span>Alle Fotos dieses Mitglieds werden sofort gelöscht. Die Einträge bleiben.</span>
          <Button size="sm" variant="destructive" disabled={busy} onClick={widerrufen}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Widerruf jetzt eintragen</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSicher(false)}>Abbrechen</Button>
        </>)}
    </div>
  );
}

export function TagebuchKommentare({ quelle, heute }) {
  const [datum, setDatum] = useState(heute);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const karte = useRef(null);
  const ersterLauf = useRef(true);
  const laden = useCallback(async () => {
    try {
      const daten = await quelle.tag(datum);
      setD(daten); setErr('');
      // Beim ersten Öffnen: zur ältesten ungelesenen Antwort springen, wenn heute keine wartet
      if (ersterLauf.current) {
        ersterLauf.current = false;
        const offen = daten.kommentare_offen?.eintraege || [];
        if (offen.length && !offen.some((o) => o.datum === datum)) setDatum(offen[offen.length - 1].datum);
        if (window.location.hash === '#tagebuch') karte.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    } catch (e) { setErr(fehlertext(e, 'Tagebuch konnte nicht geladen werden')); }
  }, [quelle, datum]);
  useEffect(() => { laden(); }, [laden]);
  const offen = d?.kommentare_offen;
  const offeneTage = [...new Set((offen?.eintraege || []).map((o) => o.datum))].sort();
  const mitKommentaren = !!offen;
  return (
    <Card id="tagebuch" ref={karte}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2"><MessageCircle className="w-4 h-4" aria-hidden="true" />{mitKommentaren || !d ? 'Tagebuch und Kommentare' : 'Tagebuch'}
            {offen?.anzahl > 0 && <Badge className="tabular-nums" aria-label={`${offen.anzahl} ungelesene Antworten`}>{offen.anzahl} neu</Badge>}</CardTitle>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Tag zurück" onClick={() => setDatum(plusTage(datum, -1))}><ChevronLeft className="w-4 h-4" /></Button>
            <Input type="date" aria-label="Tag" className="w-40" value={datum} max={heute} onChange={(e) => e.target.value && setDatum(e.target.value)} />
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Tag vor" disabled={datum >= heute} onClick={() => setDatum(plusTage(datum, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">Was das Mitglied außerhalb des Plans eingetragen hat.{mitKommentaren ? ' Dein Kommentar kommt per Push in die App; Antworten erscheinen hier.' : ''}{d?.essensfoto ? ' Foto-Einträge zeigen das Foto des Mitglieds; kcal und Nährwerte lassen sich berichtigen.' : ''}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {d?.essensfoto && <EssensfotoStand stand={d.essensfoto} quelle={quelle} onGeaendert={laden} />}
        {offeneTage.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-sm" role="status">
            <span className="text-muted-foreground">Ungelesene Antworten:</span>
            {offeneTage.map((t) => <Button key={t} size="sm" variant={t === datum ? 'secondary' : 'outline'} onClick={() => setDatum(t)}>{tagText(t)}</Button>)}
          </div>
        )}
        {err ? <p className="text-sm text-destructive" role="alert">{err}</p> : !d || d.datum !== datum ? <Skeleton className="h-16 w-full" /> : (
          <>
            {!d.eintraege.length ? <p className="text-sm text-muted-foreground">Kein Eintrag am {tagText(datum, { weekday: 'long', day: '2-digit', month: '2-digit' })}{d.wasser_ml || d.schritte ? ' — nur Wasser/Schritte' : ''}.</p> : (
              <ul className="space-y-2">
                {d.eintraege.map((e) => <Eintrag key={e.id} e={e} datum={datum} quelle={quelle} onGeaendert={laden} />)}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              {d.eintraege.length > 0 && <>Summe {d.summe.kcal} kcal{d.eintraege.some((e) => e.kcal_offen) ? ' (ohne die Einträge mit offenen kcal)' : ''} · </>}Wasser <span className="tabular-nums">{(d.wasser_ml / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} l</span> · Schritte <span className="tabular-nums">{d.schritte.toLocaleString('de-DE')}</span>
              {d.datenquellen?.length > 0 && <> · Quelle: {d.datenquellen.map((q) => `${q.name} (${q.lizenz})`).join(' · ')}</>}
            </p>
            {quelle.tageskommentar && <Tageskommentar d={d} datum={datum} quelle={quelle} onGeaendert={laden} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
